import { Dispatch } from 'redux';

import { Client, Company } from 'src/constants';
import { arrayToMap } from 'src/utils/array';

import { Events } from '../constants/webSocketConsts/events';
import { Invoice, invoicesApi } from '../services/api/invoicesApi';
import { ensureUnreachable } from '../utils/common_utils';

const updateCache = (event: Events, items: Invoice[], draft?: Invoice[]) => {
  console.info('updateCache', { draft, event, items });

  if (!draft) {
    return;
  }

  items.forEach((invoiceObj) => {
    const upcomingInvoiceIndex = draft.findIndex(
      (item: Invoice) => item.id === invoiceObj.id,
    );

    switch (event) {
      case Events.Insert: {
        if (upcomingInvoiceIndex !== -1) {
          return;
        }

        draft.unshift(invoiceObj);
        break;
      }
      case Events.Modify:
        if (upcomingInvoiceIndex === -1) {
          draft.unshift(invoiceObj);
          return;
        }
        draft.splice(
          0,
          draft.length,
          ...draft.map((item: Invoice) =>
            item.id === invoiceObj.id ? invoiceObj : item,
          ),
        );
        break;
      case Events.Remove:
        if (upcomingInvoiceIndex === -1) {
          return;
        }
        draft.splice(upcomingInvoiceIndex, 1);
        break;
      case Events.Refresh:
      case Events.DomainVerified:
        return;
      default:
        ensureUnreachable(event);
    }
  });
};

export function handleInvoiceUpdate(
  event: Events,
  items: Invoice[],
  dispatch: Dispatch<any>,
  companies: Company[],
  clients: Client[],
) {
  dispatch(
    invoicesApi.util.updateQueryData(
      'getInvoices',
      undefined,
      (draft?: Invoice[]) => {
        updateCache(event, items, draft);
      },
    ),
  );

  for (const item of items) {
    const {
      clientId: invoiceClientId,
      companyId,
      recipientId: invoiceRecipientId,
    } = item;
    const invoiceId = item.id;

    const companiesMap = arrayToMap(companies, 'id');
    // If found, this indicates that this is a company invoice
    const company = companiesMap.get(companyId || invoiceRecipientId);

    if (company && !invoiceClientId) {
      const companyClientsIds = clients
        .filter(
          (client) =>
            // TODO:(enable_many_companies) remove the companyId check once we enable many companies
            client.fields.companyId === invoiceRecipientId ||
            client.fields.companyIds?.includes(invoiceRecipientId),
        )
        .map((client) => client.id);
      for (const clientId of companyClientsIds) {
        dispatch(
          invoicesApi.util.updateQueryData(
            'getInvoicesByRecipientId',
            {
              clientId,
              companyId: company.id,
              includeCompanyInvoices: Boolean(clientId && company.id),
            },
            (draft?: Invoice[]) => {
              updateCache(event, items, draft);
            },
          ),
        );
      }
    } else {
      const queryParamClientId = invoiceClientId || invoiceRecipientId;
      dispatch(
        invoicesApi.util.updateQueryData(
          'getInvoicesByRecipientId',
          {
            clientId: queryParamClientId,
            companyId,
            includeCompanyInvoices: Boolean(queryParamClientId && companyId),
          },
          (draft?: Invoice[]) => {
            updateCache(event, items, draft);
          },
        ),
      );
    }

    dispatch(
      invoicesApi.util.updateQueryData(
        'getInvoicesByRecipientId',
        {
          clientId: undefined,
          companyId,
          includeCompanyInvoices: false,
        },
        (draft?: Invoice[]) => {
          updateCache(event, items, draft);
        },
      ),
    );

    // Updating cache for any invoices for invoice details view
    dispatch(
      invoicesApi.util.updateQueryData(
        'getInvoiceById',
        invoiceId,
        (draft?: Invoice) => {
          if (!draft) {
            return;
          }
          Object.assign(draft, item);
        },
      ),
    );
  }
}
